/**
 * Profile → Hour classes: the named windows a commitment may be rescheduled
 * into ("personal", "deep work", "errands"), plus the account timezone that
 * every commitment depends on.
 *
 * A class is deliberately MULTI-RANGE: "personal" is weekday evenings AND all
 * day at weekends, which one start/end pair cannot express. Ranges use the
 * shipped window vocabulary — minutes since local midnight, half-open and
 * end-exclusive, start > end spanning midnight, active_days a CSV of weekday
 * indices with Mon=0 and null meaning every day.
 *
 * The timezone control lives here rather than beside the other typed
 * preferences because it is the fix for a concrete failure: without an IANA
 * zone on the account nothing can be materialized at all and the commitments
 * API answers 409 saying so. It writes PUT /api/users/settings directly (a
 * partial update leaves every other setting untouched) instead of going
 * through PreferencesContext, which has no notion of the field.
 *
 * The picker's options come from GET /api/users/settings/timezones — the
 * server's own accept-list, exactly what the PUT validator checks against —
 * and filtering delegates to the curated search in ../timezone (city aliases
 * like "bombay", ambiguous abbreviations like "IST" that surface every
 * candidate for an explicit pick). What is SAVED is therefore always a
 * server-valid IANA name, never an alias or an abbreviation; the 422 branch
 * survives only as a belt-and-braces surface for a stale cached list.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';

import { useLayoutTokens } from '../hooks/useLayoutTokens';
import { getApiErrorMessage } from '../utils/apiError';
import {
  searchTimezones,
  TimezoneMatch,
  TimezoneOption,
} from '../timezone/timezoneSearch';
import {
  formatUtcOffset,
  zoneCurrentTime,
  zoneLongName,
} from '../timezone/timezoneDisplay';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export interface HourClassRange {
  id: number;
  hour_class_id: number;
  start_minute: number;
  end_minute: number;
  active_days: string | null;
}

export interface HourClass {
  id: number;
  user_id: number;
  name: string;
  is_active: boolean;
  sort_order: number;
  ranges: HourClassRange[];
}

/** Minutes since local midnight → "HH:MM" wall clock. */
const minutesToTime = (minutes: number): string => {
  const h = String(Math.floor(minutes / 60)).padStart(2, '0');
  const m = String(minutes % 60).padStart(2, '0');
  return `${h}:${m}`;
};

/** "HH:MM" → minutes since local midnight; NaN for an empty/garbled field. */
const timeToMinutes = (value: string): number => {
  const [h, m] = value.split(':').map((part) => parseInt(part, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return NaN;
  return h * 60 + m;
};

/** End before start is not an error — the range runs past midnight. */
const describeSpan = (startMinute: number, endMinute: number): string => {
  const span = `${minutesToTime(startMinute)}–${minutesToTime(endMinute)}`;
  return endMinute < startMinute ? `${span} (next day)` : span;
};

const describeDays = (activeDays: string | null): string => {
  if (!activeDays) return 'Every day';
  const labels = activeDays
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .map((part) => DAY_LABELS[Number(part)])
    .filter((label) => label !== undefined);
  return labels.length > 0 ? labels.join(', ') : 'Every day';
};

const daysFromCsv = (activeDays: string | null): number[] => {
  if (!activeDays) return [];
  return activeDays
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6);
};

const csvFromDays = (days: number[]): string | null =>
  days.length > 0 ? [...days].sort((a, b) => a - b).join(',') : null;

/** One editable range row. Times are kept as the input's own "HH:MM" text. */
interface RangeDraft {
  start: string;
  end: string;
  days: number[];
}

const draftFromRange = (range: HourClassRange): RangeDraft => ({
  start: minutesToTime(range.start_minute),
  end: minutesToTime(range.end_minute),
  days: daysFromCsv(range.active_days),
});

const rangeInvalid = (draft: RangeDraft): boolean => {
  const start = timeToMinutes(draft.start);
  const end = timeToMinutes(draft.end);
  if (Number.isNaN(start) || Number.isNaN(end)) return true;
  // Mirrors the schema: a zero-length range admits nothing and is rejected.
  return start === end;
};

/** The browser's own zone, or '' where Intl declines to name one. */
const browserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

/** ICU's canonical spelling of a zone id. Legacy and modern names resolve to
 * one form ("Asia/Calcutta" and "Asia/Kolkata" are the same zone); a name
 * Intl rejects comes back unchanged so the raw compare still applies. */
const canonicalZone = (zone: string): string => {
  try {
    return (
      new Intl.DateTimeFormat('en', { timeZone: zone }).resolvedOptions()
        .timeZone || zone
    );
  } catch {
    return zone;
  }
};

/** True only for genuinely different zones — never for a legacy/modern
 * spelling pair. The browser may report the LEGACY id (Asia/Calcutta) while
 * the picker deliberately saves the MODERN one (Asia/Kolkata); a raw string
 * compare would nag those users forever. Exported for its own tests. */
export const zonesDiffer = (a: string, b: string): boolean =>
  canonicalZone(a) !== canonicalZone(b);

const HourClassesSection: React.FC = () => {
  const { cardContentPaddingSx } = useLayoutTokens();
  const [classes, setClasses] = useState<HourClass[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState('0');
  const [ranges, setRanges] = useState<RangeDraft[]>([]);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<HourClass | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [timezone, setTimezone] = useState<string | null>(null);
  const [timezoneDraft, setTimezoneDraft] = useState('');
  const [timezoneSaving, setTimezoneSaving] = useState(false);
  const [timezoneError, setTimezoneError] = useState<string | null>(null);
  const [timezoneFeedback, setTimezoneFeedback] = useState<string | null>(null);
  const [zoneOptions, setZoneOptions] = useState<TimezoneOption[]>([]);
  // Bumped by a one-minute interval so the "Your local time" preview and the
  // per-option "now" clocks re-render; the value itself is never read.
  const [, setClockTick] = useState(0);
  const suggestedTimezone = browserTimezone();

  const fetchClasses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/hour-classes`);
      // Degrade to the empty state rather than take the Profile page down on
      // .map if a proxy hiccup serves a non-array body with a 200.
      setClasses(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load hour classes'));
    }
  }, []);

  const fetchTimezone = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/users/settings`);
      const stored = res.data?.timezone;
      const value = typeof stored === 'string' && stored ? stored : null;
      setTimezone(value);
      setTimezoneDraft(value || '');
    } catch {
      // A failed settings read must not hide the classes below it; the
      // timezone field simply starts empty and a save still works.
      setTimezone(null);
    }
  }, []);

  const fetchZoneOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/users/settings/timezones`);
      // A failed or malformed list only empties the picker; the detected-zone
      // and stored-zone paths keep working without it.
      setZoneOptions(Array.isArray(res.data) ? res.data : []);
    } catch {
      setZoneOptions([]);
    }
  }, []);

  useEffect(() => {
    fetchClasses();
    fetchTimezone();
    fetchZoneOptions();
  }, [fetchClasses, fetchTimezone, fetchZoneOptions]);

  useEffect(() => {
    const id = setInterval(() => setClockTick((tick) => tick + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const saveTimezoneValue = async (value: string) => {
    if (!value) {
      setTimezoneFeedback(null);
      setTimezoneError('Enter an IANA timezone name, for example Europe/London.');
      return;
    }
    setTimezoneSaving(true);
    try {
      // Partial update: every other setting is left untouched. The server
      // validates the name against the real zone database, so an unknown but
      // plausible zone fails here rather than inside the resolver weeks later.
      await axios.put(`${API_BASE_URL}/api/users/settings`, { timezone: value });
      setTimezone(value);
      setTimezoneDraft(value);
      setTimezoneError(null);
      setTimezoneFeedback(`Commitments will be scheduled in ${value}.`);
    } catch (err: any) {
      setTimezoneFeedback(null);
      setTimezoneError(getApiErrorMessage(err, 'Failed to save the timezone'));
    } finally {
      setTimezoneSaving(false);
    }
  };

  // The Autocomplete's value: the draft resolved against the server list. A
  // stored zone missing from a failed/stale list still renders by name; its
  // NaN offset makes every offset label degrade to nothing rather than lie.
  const selectedMatch: TimezoneMatch | null = useMemo(() => {
    if (!timezoneDraft) return null;
    const known = zoneOptions.find((option) => option.name === timezoneDraft);
    return {
      option:
        known ?? { name: timezoneDraft, offset_minutes: Number.NaN, abbreviation: null },
    };
  }, [timezoneDraft, zoneOptions]);

  const baseMatches: TimezoneMatch[] = useMemo(
    () => zoneOptions.map((option) => ({ option })),
    [zoneOptions],
  );

  const storedOption = timezone
    ? zoneOptions.find((option) => option.name === timezone)
    : undefined;
  const storedOffsetLabel = storedOption
    ? formatUtcOffset(storedOption.offset_minutes)
    : null;
  const localTimePreview = timezone ? zoneCurrentTime(timezone) : null;

  const openCreate = () => {
    setEditingId(null);
    setName('');
    setIsActive(true);
    setSortOrder('0');
    setRanges([]);
    setError(null);
    setDialogOpen(true);
  };

  const openEdit = (hourClass: HourClass) => {
    setEditingId(hourClass.id);
    setName(hourClass.name);
    setIsActive(hourClass.is_active);
    setSortOrder(String(hourClass.sort_order));
    setRanges(hourClass.ranges.map(draftFromRange));
    setError(null);
    setDialogOpen(true);
  };

  const updateRange = (index: number, patch: Partial<RangeDraft>) => {
    setRanges((prev) =>
      prev.map((range, i) => (i === index ? { ...range, ...patch } : range)),
    );
  };

  const addRange = () => {
    setRanges((prev) => [...prev, { start: '18:00', end: '22:00', days: [] }]);
  };

  const removeRange = (index: number) => {
    setRanges((prev) => prev.filter((_, i) => i !== index));
  };

  const saveDisabled = saving || name.trim() === '' || ranges.some(rangeInvalid);

  const save = async () => {
    const payload = {
      name: name.trim(),
      is_active: isActive,
      sort_order: Number(sortOrder) || 0,
      ranges: ranges.map((range) => ({
        start_minute: timeToMinutes(range.start),
        end_minute: timeToMinutes(range.end),
        active_days: csvFromDays(range.days),
      })),
    };
    setSaving(true);
    try {
      if (editingId === null) {
        await axios.post(`${API_BASE_URL}/api/hour-classes`, payload);
      } else {
        // PUT replaces the class's ranges wholesale — the same
        // client-owns-the-whole-value convention the settings namespaces use.
        await axios.put(`${API_BASE_URL}/api/hour-classes/${editingId}`, payload);
      }
      setDialogOpen(false);
      setError(null);
      await fetchClasses();
    } catch (err: any) {
      // A duplicate name is a 409; the dialog stays open for a correction.
      setError(getApiErrorMessage(err, 'Failed to save the hour class'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/hour-classes/${deleteTarget.id}`);
      setError(null);
      await fetchClasses();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to delete the hour class'));
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  // A single error slot: inside the edit dialog while it is open (a failed
  // save keeps it open), in the card body otherwise.
  const errorAlert = error && (
    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
      {error}
    </Alert>
  );

  return (
    <Card>
      <CardContent sx={cardContentPaddingSx}>
        <Typography variant="h6" gutterBottom>
          Hour classes
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Named parts of the week a commitment may be moved into. A class holds
          as many ranges as it needs — &quot;personal&quot; can be weekday
          evenings and all day at weekends at the same time.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Account timezone
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Commitments are scheduled in this zone. Until it is set nothing can
            be materialized and the commitments API answers with a 409 asking
            for it.
          </Typography>

          {timezone === null && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              No timezone is set for this account yet.
              {suggestedTimezone && (
                <Box sx={{ mt: 0.5 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => saveTimezoneValue(suggestedTimezone)}
                    disabled={timezoneSaving}
                  >
                    Use my detected timezone ({suggestedTimezone})
                  </Button>
                </Box>
              )}
            </Alert>
          )}
          {timezone !== null &&
            suggestedTimezone !== '' &&
            zonesDiffer(timezone, suggestedTimezone) && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Your browser reports {suggestedTimezone} but your account is set
                to {timezone} — traveling, or wrong setting?
                <Box sx={{ mt: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => saveTimezoneValue(suggestedTimezone)}
                    disabled={timezoneSaving}
                  >
                    Switch to {suggestedTimezone}
                  </Button>
                </Box>
              </Alert>
            )}
          {timezoneFeedback && (
            <Alert
              severity="success"
              sx={{ mb: 1 }}
              onClose={() => setTimezoneFeedback(null)}
            >
              {timezoneFeedback}
            </Alert>
          )}
          {timezoneError && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setTimezoneError(null)}>
              {timezoneError}
            </Alert>
          )}

          {timezone !== null && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2">
                Scheduling in <strong>{timezone}</strong>
                {storedOffsetLabel ? ` · ${storedOffsetLabel}` : ''}
              </Typography>
              {localTimePreview && (
                <Typography variant="body2" color="text.secondary">
                  Your local time: {localTimePreview}
                </Typography>
              )}
            </Box>
          )}

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Autocomplete
              size="small"
              options={baseMatches}
              value={selectedMatch}
              onChange={(_e, next) => {
                setTimezoneDraft(next ? next.option.name : '');
                setTimezoneError(null);
              }}
              // The curated search: IANA segments, city aliases ("bombay"),
              // and abbreviations — the latter flagged so the row says which
              // abbreviation it answers, and the user still picks a zone.
              filterOptions={(_options, state) =>
                searchTimezones(state.inputValue, zoneOptions)
              }
              getOptionLabel={(match) => match.option.name}
              isOptionEqualToValue={(a, b) => a.option.name === b.option.name}
              renderOption={(props, match) => {
                const longName = zoneLongName(match.option.name);
                const offsetLabel = formatUtcOffset(match.option.offset_minutes);
                const nowLabel = zoneCurrentTime(match.option.name);
                const detail = [offsetLabel, nowLabel ? `now ${nowLabel}` : null]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <Box
                    component="li"
                    {...props}
                    key={match.option.name}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2">
                        {match.option.name}
                        {longName ? ` — ${longName}` : ''}
                      </Typography>
                      {detail && (
                        <Typography variant="caption" color="text.secondary">
                          {detail}
                        </Typography>
                      )}
                    </Box>
                    {match.matchedAbbreviation && (
                      <Chip
                        size="small"
                        label={`matches ${match.matchedAbbreviation}`}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Timezone"
                  placeholder="Europe/London, bombay, IST…"
                />
              )}
              sx={{ minWidth: 300 }}
            />
            <Button
              variant="contained"
              onClick={() => saveTimezoneValue(timezoneDraft.trim())}
              disabled={timezoneSaving}
            >
              Save timezone
            </Button>
          </Stack>
        </Box>

        {!dialogOpen && errorAlert}

        {classes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No hour classes yet.
          </Typography>
        ) : (
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            {classes.map((hourClass) => (
              <Stack
                key={hourClass.id}
                direction="row"
                alignItems="flex-start"
                spacing={1}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1" noWrap>
                      {hourClass.name}
                    </Typography>
                    {!hourClass.is_active && <Chip size="small" label="Inactive" />}
                  </Stack>
                  {hourClass.ranges.length === 0 ? (
                    <Typography variant="body2" color="warning.main">
                      No ranges — this class admits nothing.
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {hourClass.ranges.map((range) => (
                        <Chip
                          key={range.id}
                          size="small"
                          label={`${describeSpan(
                            range.start_minute,
                            range.end_minute,
                          )} · ${describeDays(range.active_days)}`}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
                <IconButton
                  size="small"
                  aria-label={`Edit ${hourClass.name}`}
                  onClick={() => openEdit(hourClass)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={`Delete ${hourClass.name}`}
                  onClick={() => setDeleteTarget(hourClass)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}

        <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
          New hour class
        </Button>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {editingId === null ? 'New hour class' : 'Edit hour class'}
          </DialogTitle>
          <DialogContent>
            {errorAlert}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 1, mb: 2 }}
            >
              <TextField
                autoFocus
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{ flexGrow: 1, minWidth: 220 }}
              />
              <TextField
                type="number"
                label="Sort order"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                sx={{ width: 120 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="Active"
              />
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Ranges are wall-clock times. Pick no days for a range that applies
              every day; an end before the start runs past midnight.
            </Typography>

            <Stack spacing={2}>
              {ranges.map((range, idx) => (
                <Stack
                  // Index-keyed on purpose: drafts have no identity and rows
                  // can be removed from the middle.
                  key={idx}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <TextField
                    size="small"
                    type="time"
                    label={`Start ${idx + 1}`}
                    value={range.start}
                    onChange={(e) => updateRange(idx, { start: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 130 }}
                  />
                  <TextField
                    size="small"
                    type="time"
                    label={`End ${idx + 1}`}
                    value={range.end}
                    onChange={(e) => updateRange(idx, { end: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 130 }}
                  />
                  <ToggleButtonGroup
                    size="small"
                    value={range.days}
                    onChange={(_e, next) => updateRange(idx, { days: next as number[] })}
                    aria-label={`Days for range ${idx + 1}`}
                    sx={{ flexWrap: 'wrap' }}
                  >
                    {DAY_LABELS.map((label, dayIdx) => (
                      <ToggleButton
                        key={label}
                        value={dayIdx}
                        aria-label={`${label} range ${idx + 1}`}
                      >
                        {label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <IconButton
                    size="small"
                    aria-label={`Remove range ${idx + 1}`}
                    onClick={() => removeRange(idx)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  {rangeInvalid(range) && (
                    <Typography variant="caption" color="error">
                      Start and end must differ.
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>

            <Box sx={{ mt: 1.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addRange}
              >
                Add range
              </Button>
              {ranges.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  A class with no ranges admits nothing.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={saveDisabled}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
          <DialogTitle>Delete hour class?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Deleting &quot;{deleteTarget?.name}&quot; removes its ranges.
              Commitments that required this class stay exactly where they are
              and simply become unconstrained — nothing is deleted or moved.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={confirmDelete}
              disabled={deleting}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default HourClassesSection;
